import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VirtualAutocomplete from "components/VirtualAutocomplete";
import type { AutocompleteRenderInputParams } from "@mui/material";
import useChartOptions from "features/Charting/hooks/useChartOptions";
import { Metadata, MetadataType } from "types/metadata";
import useSetChartSelections from "features/Charting/hooks/useSetChartSelections";
import { useMatch } from "react-router-dom";
import Mui5 from "components/mui5";
import Grid from "components/primitives/Grid";
import MenuItem, { type MenuItemProps } from "@mui/material/MenuItem";
import type { CircuitMetric, ExerciseMetric, ExerciseRepetition } from "types";
import useChartTimePeriod from "components/charting/hooks/useChartTimePeriod";
import useChartAggregationPeriod from "components/charting/hooks/useChartAggregationPeriod";
import type { ChartingSelections } from "components/charting/types";
import useChartMeasures from "components/charting/hooks/useChartMeasures";
import useChartMetrics from "components/charting/hooks/useChartMetrics";
import DataVisualizationShowInputHelper from "components/charting/helpers/DataVisualizationShowInputHelper";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import useChartRepMetrics from "components/charting/hooks/useChartRepMetrics";
import ChartHelpers from "features/Charting/helpers/ChartHelpers";
import PopoverDateRangePicker from "components/Inputs/PopoverDateRangePicker";
import useChartSelections from "components/charting/hooks/useChartSelections";
import moment from "moment";
import AthleteProfileChartSetSelector from "./AthleteProfileChartSetSelector";
import AthleteProfileChartProfilingSetSelector from "./AthleteProfileChartProfilingSetSelector";
import AthleteProfileChartProfilingCompareSetSelector from "./AthleteProfileChartProfilingCompareSetSelector";
import AthleteProfileChartSetSelectorV1 from "./AthleteProfileChartSetSelectorV1";
import AthleteProfileChartCompareSetSelectorV1 from "./AthleteProfileChartCompareSetSelectorV1";
import { athleteProfilingV2Enabled, athlete1RMV2Enabled } from "config/features";
import { metadataAutocompleteFilterOptions } from "config/autocomplete";
import { useTranslation } from "react-i18next";
import logEvent from "utils/logEvent";
import useProfileTypeQueryState, { ProfileType } from "components/charting/hooks/useProfileTypeQueryState";
import SelectProfileType from "./components/SelectProfileType";
import AthleteProfileChartHelpers from "../helpers/AthleteProfileChartHelpers";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import OutlinedTextField from "components/Inputs/OutlinedTextField";
import { OneRepMaxChartNoResults, ProfilingChartNoResults } from "./components/AutocompleteNoResults";
import OneRepMaxSourceToggle from "./components/OneRepMaxSourceToggle";

interface AthleteProfileChartControlsProps {}

const ChartSetSelector = athleteProfilingV2Enabled ? AthleteProfileChartSetSelector : AthleteProfileChartSetSelectorV1;
const ChartProfilingSetSelector = athleteProfilingV2Enabled
  ? AthleteProfileChartProfilingSetSelector
  : AthleteProfileChartSetSelectorV1;
const ChartProfilingCompareSetSelector = athleteProfilingV2Enabled
  ? AthleteProfileChartProfilingCompareSetSelector
  : AthleteProfileChartCompareSetSelectorV1;

function AthleteProfileChartControls(props: AthleteProfileChartControlsProps) {
  const { t } = useTranslation();
  const options = useChartOptions();
  const setSelections = useSetChartSelections();
  const athleteId = useMatch("/athlete-hub/:athleteId")?.params?.athleteId;
  const chartTimePeriod = useChartTimePeriod();
  const chartAggregationPeriod = useChartAggregationPeriod();
  const chartMeasures = useChartMeasures();
  const chartMetrics = useChartMetrics();
  const chartRepMetrics = useChartRepMetrics();
  const visType = useDataVisualizationType();
  const showInput = DataVisualizationShowInputHelper.show(visType);
  const isProfilingChart = visType === DataVisualizationType.PROFILING_CHART;
  const is1RMChart = visType === DataVisualizationType.ONE_REP_MAX_CHART;
  const timeSelectorRef = useRef<HTMLDivElement>(null);
  const [timePickerOpen, setPickerOpen] = useState(false);
  const selections = useChartSelections();
  const [selectedProfileType, setSelectedProfileType] = useProfileTypeQueryState();

  useEffect(() => {
    const source = options.sources.find((athlete) => athlete.id === athleteId);
    if (!source) return;

    setSelections({ sources: [source] });
  }, [athleteId, options.sources, setSelections]);

  const exerciseOptions = useMemo(
    () => ChartHelpers.filterMeasuresByDataVisType(options.measures, { visType }),
    [options.measures, visType]
  );

  // Labels are not unique (custom exercises can share a name), so key options by id.
  const getOptionKey = useCallback((option: Metadata) => option.id, []);

  const chartMeasureValue = chartMeasures.values[0];

  const repMetricOptions = useMemo(() => {
    if (chartMeasureValue.type !== MetadataType.EXERCISE) {
      return [];
    }
    return ChartHelpers.filterProfilingRepMetrics(chartMeasureValue.metadata.repetitions, { visType });
  }, [chartMeasureValue.metadata, chartMeasureValue.type, visType]);

  const customPeriodOption = useMemo(() => ({ label: t("global.custom_label"), value: null }), [t]);

  const setPeriod = useCallback(
    (period: ChartingSelections["period"]) => {
      if (period.value === null) {
        setPickerOpen(true);
        return;
      }

      const endDate = moment().endOf("day");
      const startDate = moment().endOf("day").clone().subtract(1, period.value);

      setSelections({
        period,
        startDate,
        endDate,
      });
    },
    [setSelections]
  );

  const exerciseOptionsValue = useMemo(
    () => exerciseOptions.find((option) => option.id === chartMeasureValue.id) || null,
    [exerciseOptions, chartMeasureValue.id]
  );

  const handleSetPopoverPeriod = useCallback(
    (startDate: moment.Moment, endDate: moment.Moment) => {
      setSelections({
        period: customPeriodOption,
        startDate,
        endDate,
      });
    },
    [customPeriodOption, setSelections]
  );

  /**
   * This is a workaround to ensure that the custom period option is displayed correctly in the select input.
   * It would need further refactoring to allow custom periods and query url parameters to work together seamlessly.
   */
  const menuSelectorTimeValue = useMemo(() => {
    return selections.period.value === null ? customPeriodOption : selections.period;
  }, [selections.period, customPeriodOption]);

  const handleOnSelectRepMetricChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | (Event & { target: { value: unknown; name: string } })) => {
      const value = event.target.value as unknown as ExerciseRepetition;

      logEvent("ATHLETE_CHARTING_VIEW_REP_METRIC", {
        chartType: visType,
        visType: visType,
        metricField: value.field,
        exerciseId: chartMeasureValue.id ?? "",
        exerciseCategory:
          chartMeasureValue.type === MetadataType.EXERCISE ? chartMeasureValue.metadata?.category : undefined,
      });

      return chartRepMetrics.onChangeAtIndex(0)(event, value);
    },
    [chartMeasureValue.id, chartMeasureValue.metadata, chartMeasureValue.type, chartRepMetrics, visType]
  );

  const handleOnSelectMetricChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | (Event & { target: { value: unknown; name: string } })) => {
      const value = event.target.value as unknown as ExerciseMetric | CircuitMetric;

      logEvent("ATHLETE_CHARTING_VIEW_METRIC", {
        chartType: visType,
        visType: visType,
        metricField: value.field,
        exerciseId: chartMeasureValue.id ?? "",
        exerciseCategory:
          chartMeasureValue.type === MetadataType.EXERCISE ? chartMeasureValue.metadata?.category : undefined,
      });

      return chartMetrics.onChangeAtIndex(0)(event, value);
    },
    [chartMeasureValue.id, chartMeasureValue.metadata, chartMeasureValue.type, chartMetrics, visType]
  );

  const handleOnProfileTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | (Event & { target: { value: unknown; name: string } })) => {
      const value = event.target.value as unknown as ProfileType;

      setSelectedProfileType(value);

      const exercise = chartMeasureValue.type === MetadataType.EXERCISE ? chartMeasureValue.metadata : null;

      logEvent("ATHLETE_CHARTING_VIEW_PROFILE_TYPE", {
        chartType: visType,
        visType: visType,
        profileType: value,
        metricField: AthleteProfileChartHelpers.getProfileTypeRepMetric(exercise, value)?.field ?? "",
        exerciseId: chartMeasureValue.id ?? "",
        exerciseCategory: exercise?.category,
      });
    },
    [chartMeasureValue.id, chartMeasureValue.metadata, chartMeasureValue.type, setSelectedProfileType, visType]
  );

  const noOptionsText = useMemo(() => {
    if (isProfilingChart && athleteProfilingV2Enabled) {
      return <ProfilingChartNoResults />;
    }

    if (is1RMChart && athlete1RMV2Enabled) {
      return <OneRepMaxChartNoResults />;
    }
  }, [is1RMChart, isProfilingChart]);

  const memoSlotProps = useMemo(() => {
    return {
      paper: { sx: { width: 415 } },
    } as const;
  }, []);

  const renderMeasurementInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <OutlinedTextField
          {...params}
          sx={{ minWidth: 240 }}
          label={t("athlete_profile_chart_controls.measurement_select_input_label")}
          fullWidth={false}
          color="primary"
          slotProps={{
            input: {
              ...params.InputProps,
              placeholder: t("global.search_exercises_placeholder"),
            },
          }}
        />
      );
    },
    [t]
  );

  return (
    <Grid container spacing="12px" sx={{ alignItems: "center" }}>
      {showInput.exercise && (
        <Grid>
          <ThemeProvider2024>
            <VirtualAutocomplete
              disablePortal
              filterOptions={metadataAutocompleteFilterOptions}
              disableClearable={!!chartMeasureValue.id}
              options={exerciseOptions}
              onChange={chartMeasures.onChangeAtIndex(0)}
              value={exerciseOptionsValue}
              getOptionLabel={chartMeasures.getOptionLabel}
              getOptionKey={getOptionKey}
              groupBy={chartMeasures.groupBy}
              noOptionsText={noOptionsText}
              slotProps={memoSlotProps}
              renderInput={renderMeasurementInput}
            />
          </ThemeProvider2024>
        </Grid>
      )}
      {showInput.metric && (
        <>
          {(chartMeasureValue.type === MetadataType.EXERCISE ||
            chartMeasureValue.type === MetadataType.CIRCUIT ||
            !chartMeasureValue.type) && (
            <Grid>
              <Mui5.TextField
                sx={(theme) => ({ minWidth: theme.spacing(14) })}
                label={t("athlete_profile_chart_controls.metric_select_label")}
                fullWidth={false}
                size="small"
                color="primary"
                select
                slotProps={{ select: { onChange: handleOnSelectMetricChange } }}
                value={chartMeasureValue.metric || ""}
                disabled={!chartMeasureValue?.metadata}
              >
                {(chartMeasureValue.metadata?.metrics || []).map((metric) => (
                  <MenuItem key={metric.field} value={metric as unknown as MenuItemProps["value"]}>
                    {metric.name}
                  </MenuItem>
                ))}
              </Mui5.TextField>
            </Grid>
          )}
        </>
      )}
      {showInput.profileType && (
        <Grid>
          <SelectProfileType
            value={selectedProfileType}
            onChange={handleOnProfileTypeChange}
            disabled={!chartMeasureValue?.metadata}
          />
        </Grid>
      )}
      {showInput.repMetric && (
        <>
          {(chartMeasureValue.type === MetadataType.EXERCISE || !chartMeasureValue.type) && (
            <Grid>
              <Mui5.TextField
                sx={(theme) => ({ minWidth: theme.spacing(14) })}
                label={t("athlete_profile_chart_controls.rep_metric_select_label")}
                fullWidth={false}
                size="small"
                color="primary"
                select
                slotProps={{ select: { onChange: handleOnSelectRepMetricChange } }}
                value={chartMeasureValue.repMetric || ""}
                disabled={!chartMeasureValue?.metadata}
              >
                {repMetricOptions.map((repMetric) => (
                  <MenuItem key={repMetric.field} value={repMetric as unknown as MenuItemProps["value"]}>
                    {repMetric.name}
                  </MenuItem>
                ))}
              </Mui5.TextField>
            </Grid>
          )}
        </>
      )}
      {showInput.sets && (
        <Grid size={{ xs: 12, lg: "grow" }}>
          <Grid container spacing="12px">
            <Grid size={{ xs: 6 }} sx={{ maxWidth: "400px" }}>
              {isProfilingChart ? <ChartProfilingSetSelector /> : <ChartSetSelector />}
            </Grid>
            {showInput.comparableSets && (
              <Grid size={{ xs: 6 }} sx={{ maxWidth: "400px" }}>
                <ChartProfilingCompareSetSelector />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {showInput.oneRepMaxSourceToggle ? (
        <Grid>
          <OneRepMaxSourceToggle />
        </Grid>
      ) : null}
      <Grid container justifyContent="flex-end" sx={{ marginLeft: "auto" }}>
        {showInput.aggregation && (
          <Grid>
            <Mui5.TextField
              sx={(theme) => ({ minWidth: theme.spacing(14) })}
              label={t("athlete_profile_chart_controls.aggregation_select_label")}
              fullWidth={false}
              size="small"
              color="primary"
              select
              value={chartAggregationPeriod.value}
              slotProps={{
                select: {
                  onChange: (event) => {
                    return chartAggregationPeriod.onChange(
                      event,
                      event.target.value as ChartingSelections["aggregationPeriod"]
                    );
                  },
                },
              }}
            >
              {chartAggregationPeriod.options.map((option) => (
                <MenuItem key={option.value} value={option as unknown as MenuItemProps["value"]}>
                  {typeof option.label === "string" ? t(option.label) : option.label}
                </MenuItem>
              ))}
            </Mui5.TextField>
          </Grid>
        )}
        {showInput.period && (
          <Grid>
            <Mui5.TextField
              ref={timeSelectorRef}
              sx={(theme) => ({ minWidth: theme.spacing(14) })}
              label={t("athlete_profile_chart_controls.time_select_label")}
              fullWidth={false}
              size="small"
              color="primary"
              select
              value={menuSelectorTimeValue}
              slotProps={{
                select: {
                  onChange: (event) => setPeriod(event.target.value as ChartingSelections["period"]),
                },
              }}
            >
              {chartTimePeriod.options.map((option) => (
                <MenuItem key={option.value} value={option as unknown as MenuItemProps["value"]}>
                  {typeof option.label === "string" ? t(option.label) : option.label}
                </MenuItem>
              ))}
              <MenuItem
                key={customPeriodOption.value}
                value={customPeriodOption as unknown as MenuItemProps["value"]}
                onClick={() => setPeriod(customPeriodOption)}
              >
                {customPeriodOption.label}
              </MenuItem>
            </Mui5.TextField>

            <PopoverDateRangePicker
              anchorEl={timeSelectorRef.current}
              endDate={selections.endDate}
              open={timePickerOpen}
              setPeriod={handleSetPopoverPeriod}
              setOpen={setPickerOpen}
              startDate={selections.startDate}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default AthleteProfileChartControls;
